import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, ListGroup, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import { FaClipboardList, FaFileAlt, FaHistory, FaPlay, FaShieldAlt, FaUsers } from 'react-icons/fa';
import { useMsal } from '@azure/msal-react';
import { getMyHighestRole, getReportDefinitions, triggerReport, ReportDefinition } from '@/api/adminApi';
import { searchUsers } from '@/services/resourcesApi';
import './GroupReportBar.scss';

// ---- Public types ----

export interface GroupContext {
  siteUrl?: string | null;
  groupId?: string | null;
  displayName?: string | null;
}

interface Props {
  context: GroupContext;
  /** compact=true → icon-only buttons (for detail panels); false → icon + label (for admin pages) */
  compact?: boolean;
}

// ---- Internal types ----

interface PropertyEntry {
  key: string;
  label: string;
  defaultValue: unknown;
  isBooleanLike: boolean;
  isArray: boolean;
  isInternal: boolean;
}

interface UserSearchResult {
  id?: string;
  displayName?: string;
  mail?: string;
  userPrincipalName?: string;
  email?: string;
  upn?: string;
}

interface RecipientSuggestion {
  id: string;
  displayName: string;
  email: string;
}

// ---- Helpers ----

function safeParseJson(value?: string | null): unknown {
  if (!value?.trim()) return undefined;
  try { return JSON.parse(value); } catch { return undefined; }
}

function normalizeKey(key: string): string {
  return key.replace(/[_\s-]/g, '').toLowerCase();
}

const INTERNAL_KEYS = new Set(['debug', 'debuglevel', 'overridedebug', 'overridetest', 'test']);

function extractPropertyEntries(report: ReportDefinition): PropertyEntry[] {
  const parsed = safeParseJson(report.reportOptions);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  const props = (parsed as Record<string, unknown>).reportProperties;
  if (!props || typeof props !== 'object' || Array.isArray(props)) return [];

  return Object.entries(props as Record<string, unknown>).map(([key, rawValue]) => {
    const norm = normalizeKey(key);
    const isArray = Array.isArray(rawValue);
    const isBooleanLike =
      !isArray &&
      (typeof rawValue === 'boolean' ||
        (typeof rawValue === 'string' && ['true', 'false'].includes(rawValue.toLowerCase())));

    // Humanise camelCase key → "Camel Case"
    const label = key.replace(/([A-Z])/g, ' $1').trim();

    return { key, label, defaultValue: rawValue, isBooleanLike, isArray, isInternal: INTERNAL_KEYS.has(norm) };
  });
}

/** Returns the full property map pre-populated with group context values. */
function buildPropertyValues(
  entries: PropertyEntry[],
  context: GroupContext
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const e of entries) {
    const norm = normalizeKey(e.key);
    if (norm === 'siteurl' && context.siteUrl) {
      result[e.key] = context.siteUrl;
    } else if (norm === 'groupid' && context.groupId) {
      result[e.key] = context.groupId;
    } else if (
      ['displayname', 'groupname', 'grouptitle', 'sitetitle'].includes(norm) &&
      context.displayName
    ) {
      result[e.key] = context.displayName;
    } else {
      result[e.key] = e.defaultValue;
    }
  }
  return result;
}

function getReportSettings(report: ReportDefinition): Record<string, unknown> {
  const s = safeParseJson(report.reportSettings);
  return s && typeof s === 'object' && !Array.isArray(s) ? (s as Record<string, unknown>) : {};
}

function isGroupEnabled(report: ReportDefinition): boolean {
  const v = getReportSettings(report).groupTriggerEnabled;
  return v === true || String(v).toLowerCase() === 'true';
}

function isReportManualTriggerDisabled(report: ReportDefinition): boolean {
  const settings = getReportSettings(report);
  const v = settings.manualTriggerDisabled;
  return v === true || String(v).toLowerCase() === 'true';
}

function parseSendRunAfterSummaryEmails(runAfter?: string): string[] | null {
  if (!runAfter?.trim()) return null;
  try {
    const parsed = JSON.parse(runAfter) as Record<string, unknown>;
    if (parsed.action !== 'sendRunAfterSummary') return null;
    const email = parsed.email as Record<string, unknown> | undefined;
    const raw = typeof email?.recipientEmail === 'string' ? email.recipientEmail : '';
    return raw
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter(Boolean);
  } catch {
    return null;
  }
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

// ---- Icon registry ----

const ICON_MAP: Record<string, React.ReactNode> = {
  inventory: <FaClipboardList />,
  list:      <FaClipboardList />,
  files:     <FaFileAlt />,
  shield:    <FaShieldAlt />,
  users:     <FaUsers />,
  history:   <FaHistory />,
};

function getReportIcon(iconKey?: string): React.ReactNode {
  if (!iconKey) return <FaPlay />;
  return ICON_MAP[iconKey.toLowerCase()] ?? <FaPlay />;
}

// ---- Component ----

export function GroupReportBar({ context, compact = true }: Props) {
  const { accounts } = useMsal();
  const currentUserEmail = (accounts[0]?.username ?? '').trim();

  const [reports, setReports] = useState<ReportDefinition[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [selectedReport, setSelectedReport] = useState<ReportDefinition | null>(null);
  const [allPropertyValues, setAllPropertyValues] = useState<Record<string, unknown>>({});
  const [runAfterRecipients, setRunAfterRecipients] = useState<string[]>([]);
  const [runAfterSearchTerm, setRunAfterSearchTerm] = useState('');
  const [recipientSuggestions, setRecipientSuggestions] = useState<RecipientSuggestion[]>([]);
  const [searchingRecipients, setSearchingRecipients] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    Promise.all([
      getReportDefinitions(),
      getMyHighestRole().catch(() => ({ role: 'User' }))
    ])
      .then(([allReports, roleData]) => {
        setReports(allReports.filter(isGroupEnabled));
        const role = String(roleData?.role ?? '').trim().toLowerCase();
        setIsAdminUser(role === 'admin' || role === 'superadmin');
        setIsSuperAdmin(role === 'superadmin');
      })
      .catch(() => setReports([]))
      .finally(() => setLoadingReports(false));
  }, []);

  // Enrich each report with its settings metadata
  const enriched = useMemo(
    () =>
      reports.map((report) => {
        const settings = getReportSettings(report);
        return {
          report,
          label:
            typeof settings.groupTriggerLabel === 'string'
              ? settings.groupTriggerLabel
              : report.reportTitle,
          iconKey:
            typeof settings.groupTriggerIcon === 'string' ? settings.groupTriggerIcon : undefined,
        };
      }),
    [reports]
  );

  // All entries for the selected report (includes internal + array — needed for payload)
  const allEntries = useMemo(
    () => (selectedReport ? extractPropertyEntries(selectedReport) : []),
    [selectedReport]
  );

  // Only the entries shown in the UI (non-internal, non-array)
  const visibleEntries = useMemo(
    () => allEntries.filter((e) => !e.isInternal && !e.isArray),
    [allEntries]
  );

  const requiresSummaryEmails = useMemo(
    () => parseSendRunAfterSummaryEmails(selectedReport?.runAfter) !== null,
    [selectedReport]
  );

  const selectedReportIsLocked = useMemo(
    () => Boolean(selectedReport && isReportManualTriggerDisabled(selectedReport)),
    [selectedReport]
  );

  const selectedReportIsRestricted = useMemo(
    () => Boolean(selectedReportIsLocked && !isSuperAdmin),
    [selectedReportIsLocked, isSuperAdmin]
  );

  const isCurrentUserRecipient = useMemo(() => {
    if (!currentUserEmail) return false;
    const me = normalizeEmail(currentUserEmail);
    return runAfterRecipients.some((email) => normalizeEmail(email) === me);
  }, [currentUserEmail, runAfterRecipients]);

  const endUserMustKeepSelf = Boolean(requiresSummaryEmails && !isAdminUser && currentUserEmail);

  useEffect(() => {
    if (!selectedReport || !requiresSummaryEmails) {
      setRecipientSuggestions([]);
      setSearchingRecipients(false);
      return;
    }

    const term = runAfterSearchTerm.trim();
    if (term.length < 2) {
      setRecipientSuggestions([]);
      setSearchingRecipients(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearchingRecipients(true);
        const results = await searchUsers(term);
        if (cancelled) return;

        const existing = new Set(runAfterRecipients.map(normalizeEmail));
        const suggestions = (results as UserSearchResult[])
          .map((user, index) => {
            const email = (user.mail || user.userPrincipalName || user.email || user.upn || '').trim();
            if (!email) return null;
            return {
              id: user.id || `${email}-${index}`,
              displayName: (user.displayName || email).trim(),
              email,
            };
          })
          .filter((item): item is RecipientSuggestion => Boolean(item))
          .filter((item) => !existing.has(normalizeEmail(item.email)))
          .slice(0, 8);

        setRecipientSuggestions(suggestions);
      } catch {
        if (!cancelled) setRecipientSuggestions([]);
      } finally {
        if (!cancelled) setSearchingRecipients(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [selectedReport, requiresSummaryEmails, runAfterSearchTerm, runAfterRecipients]);

  function openModal(report: ReportDefinition) {
    const entries = extractPropertyEntries(report);
    setAllPropertyValues(buildPropertyValues(entries, context));

    const configuredRecipients = parseSendRunAfterSummaryEmails(report.runAfter);
    if (configuredRecipients !== null) {
      setRunAfterRecipients(currentUserEmail ? [currentUserEmail] : []);
    } else {
      setRunAfterRecipients([]);
    }

    setRunAfterSearchTerm('');
    setRecipientSuggestions([]);
    setSearchingRecipients(false);
    setError(null);
    setSuccess(false);
    setSelectedReport(report);
  }

  function closeModal() {
    setSelectedReport(null);
    setBusy(false);
    setError(null);
    setSuccess(false);
    setRunAfterRecipients([]);
    setRunAfterSearchTerm('');
    setRecipientSuggestions([]);
    setSearchingRecipients(false);
  }

  function addRecipientsFromInput(value?: string) {
    const parsed = (value ?? runAfterSearchTerm)
      .split(/[;,]/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (parsed.length === 0) return;

    setRunAfterRecipients((prev) => {
      const existing = new Set(prev.map(normalizeEmail));
      const merged = [...prev];
      for (const candidate of parsed) {
        const normalized = normalizeEmail(candidate);
        if (!existing.has(normalized)) {
          merged.push(candidate);
          existing.add(normalized);
        }
      }
      return merged;
    });

    setRunAfterSearchTerm('');
    setRecipientSuggestions([]);
  }

  function handleSelectRecipientSuggestion(suggestion: RecipientSuggestion) {
    addRecipientsFromInput(suggestion.email);
  }

  function handleRecipientSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (recipientSuggestions.length > 0) {
      handleSelectRecipientSuggestion(recipientSuggestions[0]);
      return;
    }

    addRecipientsFromInput();
  }

  function removeRecipient(emailToRemove: string) {
    const removeNorm = normalizeEmail(emailToRemove);
    if (endUserMustKeepSelf && removeNorm === normalizeEmail(currentUserEmail)) {
      return;
    }

    setRunAfterRecipients((prev) => prev.filter((email) => normalizeEmail(email) !== removeNorm));
  }

  async function handleTrigger() {
    if (!selectedReport) return;

    if (selectedReportIsRestricted) {
      setError('This report can only be manually triggered by a Super Admin.');
      return;
    }

    if (requiresSummaryEmails) {
      if (runAfterRecipients.length === 0) {
        setError('At least one recipient email is required for this report.');
        return;
      }
      if (endUserMustKeepSelf && !isCurrentUserRecipient) {
        setError('You must remain in the recipient list so you receive a copy of this report.');
        return;
      }
    }

    try {
      setBusy(true);
      setError(null);

      const baseOptions = safeParseJson(selectedReport.reportOptions);
      const optionsPayload: Record<string, unknown> =
        baseOptions && typeof baseOptions === 'object' && !Array.isArray(baseOptions)
          ? { ...(baseOptions as Record<string, unknown>) }
          : {};

      if (requiresSummaryEmails) {
        const runAfterParsed = safeParseJson(selectedReport.runAfter) as Record<string, unknown> | undefined;
        optionsPayload.runAfter = {
          ...(runAfterParsed ?? {}),
          email: {
            ...((runAfterParsed?.email ?? {}) as object),
            recipientEmail: runAfterRecipients.join(';')
          }
        };
      }

      await triggerReport(
        selectedReport.m365ReportId,
        JSON.stringify(optionsPayload),
        JSON.stringify(allPropertyValues)
      );
      setSuccess(true);
      setTimeout(closeModal, 2000);
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to trigger report. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function updateValue(key: string, value: unknown) {
    setAllPropertyValues((prev) => ({ ...prev, [key]: value }));
  }

  function getBooleanChecked(entry: PropertyEntry): boolean {
    const v = allPropertyValues[entry.key] ?? entry.defaultValue;
    if (typeof v === 'boolean') return v;
    return String(v).toLowerCase() === 'true';
  }

  if (loadingReports || reports.length === 0) return null;

  const selectedSettings = selectedReport ? getReportSettings(selectedReport) : {};

  return (
    <>
      <div className={`group-report-bar${compact ? ' compact' : ' full'}`}>
        {enriched.map(({ report, label, iconKey }) => (
          <OverlayTrigger
            key={report.m365ReportId}
            placement="top"
            overlay={<Tooltip id={`grp-rpt-tip-${report.m365ReportId}`}>{label}</Tooltip>}
          >
            <button
              type="button"
              className="group-report-btn"
              onClick={() => openModal(report)}
              aria-label={label}
            >
              {getReportIcon(iconKey)}
              {!compact && <span className="group-report-btn-label">{label}</span>}
            </button>
          </OverlayTrigger>
        ))}
      </div>

      <Modal show={Boolean(selectedReport)} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6 d-flex align-items-center gap-2">
            {getReportIcon(
              typeof selectedSettings.groupTriggerIcon === 'string'
                ? selectedSettings.groupTriggerIcon
                : undefined
            )}
            <span>{selectedReport?.reportTitle ?? ''}</span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {success ? (
            <Alert variant="success" className="mb-0">
              Report run queued successfully.
            </Alert>
          ) : (
            <>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {context.displayName && (
                <p className="text-muted small mb-3">
                  Target: <strong>{context.displayName}</strong>
                </p>
              )}

              {selectedReportIsLocked && !isSuperAdmin && (
                <Alert variant="warning" className="mb-3">
                  <strong>Manual trigger is disabled.</strong> This report is locked and can only be run by a Super Admin.
                </Alert>
              )}

              {selectedReportIsLocked && isSuperAdmin && (
                <Alert variant="warning" className="mb-3 border border-warning-subtle bg-warning-subtle">
                  <strong>Locked report: Super Admin override.</strong> This report is locked, but your current role allows manual trigger. Please be careful when running it.
                </Alert>
              )}

              {!selectedReportIsRestricted && requiresSummaryEmails && (
                <Form.Group className="mb-3">
                  <Form.Label className="small fw-semibold mb-1">
                    Recipient Emails <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="recipient-suggestions-container">
                    <Form.Control
                      type="search"
                      size="sm"
                      placeholder="Type a name or email to search users..."
                      value={runAfterSearchTerm}
                      onChange={(e) => setRunAfterSearchTerm(e.target.value)}
                      onKeyDown={handleRecipientSearchKeyDown}
                    />
                    {searchingRecipients && (
                      <div className="small text-muted mt-2">Searching users...</div>
                    )}
                    {!searchingRecipients && recipientSuggestions.length > 0 && (
                      <ListGroup className="recipient-suggestions-dropdown mt-1">
                        {recipientSuggestions.map((suggestion) => (
                          <ListGroup.Item
                            key={suggestion.id}
                            action
                            onClick={() => handleSelectRecipientSuggestion(suggestion)}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <span>{suggestion.displayName}</span>
                            <span className="text-muted small">{suggestion.email}</span>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </div>

                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {runAfterRecipients.length === 0 ? (
                      <span className="text-muted small">No recipients selected.</span>
                    ) : (
                      runAfterRecipients.map((email) => {
                        const isSelf = currentUserEmail && normalizeEmail(email) === normalizeEmail(currentUserEmail);
                        const disableRemove = Boolean(endUserMustKeepSelf && isSelf);
                        return (
                          <span key={email} className="badge text-bg-light border d-inline-flex align-items-center gap-2 py-2 px-2">
                            <span>{email}</span>
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-decoration-none"
                              onClick={() => removeRecipient(email)}
                              disabled={disableRemove}
                              title={disableRemove ? 'End users must receive a copy of their generated reports.' : 'Remove recipient'}
                            >
                              ×
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>

                  <Form.Text className="text-muted">
                    {isAdminUser
                      ? 'Admins can add or remove recipients, including themselves.'
                      : 'You can add recipients, but you will always receive a copy of the report.'} Search by name/email and press Enter to add.
                  </Form.Text>
                </Form.Group>
              )}

              {visibleEntries.length > 0 ? (
                <fieldset disabled={selectedReportIsRestricted} style={selectedReportIsRestricted ? { opacity: 0.6 } : undefined}>
                  {visibleEntries.map((entry) =>
                  entry.isBooleanLike ? (
                    <Form.Group key={entry.key} className="mb-2">
                      <Form.Check
                        type="switch"
                        id={`grp-prop-${entry.key}`}
                        label={entry.label}
                        checked={getBooleanChecked(entry)}
                        onChange={(e) =>
                          updateValue(
                            entry.key,
                            typeof entry.defaultValue === 'boolean'
                              ? e.target.checked
                              : e.target.checked
                              ? 'true'
                              : 'false'
                          )
                        }
                      />
                    </Form.Group>
                  ) : (
                    <Form.Group key={entry.key} className="mb-3">
                      <Form.Label className="small fw-semibold mb-1">{entry.label}</Form.Label>
                      <Form.Control
                        type={typeof entry.defaultValue === 'number' ? 'number' : 'text'}
                        size="sm"
                        value={String(allPropertyValues[entry.key] ?? entry.defaultValue ?? '')}
                        onChange={(e) =>
                          updateValue(
                            entry.key,
                            typeof entry.defaultValue === 'number'
                              ? Number(e.target.value)
                              : e.target.value
                          )
                        }
                      />
                    </Form.Group>
                  )
                )}
                </fieldset>
              ) : (
                <p className="text-muted small">No configurable properties for this report.</p>
              )}
            </>
          )}
        </Modal.Body>

        {!success && (
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={closeModal} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="sm"
              className="text-white group-report-trigger-btn"
              onClick={handleTrigger}
              disabled={busy || selectedReportIsRestricted}
            >
              {busy && <Spinner size="sm" animation="border" className="me-1 group-report-loading-spinner" />}
              {busy ? 'Queuing…' : 'Trigger Report'}
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
}
